import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Gift, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSeo } from '../hooks/useSeo';
import { useToast } from '../hooks/use-toast';
import { BalanceCheck } from '../components/BalanceCheck';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = 'w-full bg-[#FBF7F0] border border-[#1E1B18]/15 focus:border-[#8B7FB8] outline-none rounded-sm px-4 py-3 text-[14px] text-[#1E1B18] transition-colors';

const Field = ({ label, children }) => (
  <label className="block"><span className="block text-[11px] tracking-[0.2em] text-[#1E1B18]/55 mb-2">{label}</span>{children}</label>
);

export default function GiftCards() {
  useSeo('Massage Gift Cards Mississauga | Aura Lune RMT', 'Give the gift of registered massage therapy in Mississauga. Buy an Aura Lune RMT gift card online.');
  const { toast } = useToast();
  const [opts, setOpts] = useState({ presets: [50, 100, 150], min: 25, max: 1000 });
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState('');
  const [form, setForm] = useState({ recipient_name: '', recipient_email: '', sender_name: '', sender_email: '', message: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${API}/gift-cards/options`).then((r) => setOpts(r.data)).catch(() => {}); }, []);
  const value = custom ? parseInt(custom, 10) : amount;
  const valid = value >= opts.min && value <= opts.max;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast({ title: `Choose an amount between $${opts.min} and $${opts.max}`, variant: 'destructive' }); return;
    }
    setBusy(true);
    try {
      const body = { ...form, amount: value, origin_url: window.location.origin, recipient_email: form.recipient_email || null, message: form.message || null };
      const r = await axios.post(`${API}/gift-cards/checkout`, body);
      window.location.href = r.data.checkout_url;
    } catch {
      toast({ title: 'Could not start checkout', description: 'Please check the form and try again.', variant: 'destructive' });
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#F4EFE7] min-h-screen overflow-x-hidden" data-testid="gift-cards-page">
      <Navbar />
      <section className="pt-32 pb-20 max-w-[1100px] mx-auto px-6 sm:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-14">
        <div>
          <p className="text-[11px] tracking-[0.3em] text-[#8B7FB8] font-medium mb-5 flex items-center gap-2"><Gift size={13} /> GIFT CARDS</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1E1B18] leading-[1.05] mb-5">Give the gift of calm</h1>
          <p className="text-[15px] text-[#1E1B18]/65 leading-relaxed mb-10 max-w-md">
            An Aura Lune gift card can be used for any massage. After payment you get a printable gift card with a unique code to give or forward.
          </p>
          <div className="aspect-[1.6] max-w-md rounded-md bg-[#0F0E0C] text-[#F4EFE7] p-7 flex flex-col justify-between shadow-xl">
            <p className="text-[11px] tracking-[0.3em] text-[#F4EFE7]/60">AURA LUNE · RMT</p>
            <div>
              <p data-testid="gift-preview-amount" className="font-serif text-5xl">${valid ? value : '—'}</p>
              <p className="text-[13px] text-[#F4EFE7]/60 mt-2">For {form.recipient_name || 'someone special'}</p>
            </div>
          </div>
          <div className="mt-12 max-w-md hidden lg:block"><BalanceCheck /></div>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="gift-card-form">
          <Field label="AMOUNT (CAD)">
            <div className="flex flex-wrap gap-2">
              {opts.presets.map((p) => (
                <button type="button" key={p} data-testid={`gift-amount-${p}`} onClick={() => { setAmount(p); setCustom(''); }}
                  className={`px-5 py-2.5 rounded-full text-[13px] font-medium border transition-colors ${!custom && amount === p ? 'bg-[#0F0E0C] text-[#F4EFE7] border-[#0F0E0C]' : 'border-[#1E1B18]/20 text-[#1E1B18]/75 hover:border-[#1E1B18]/50'}`}>
                  ${p}
                </button>
              ))}
              <input data-testid="gift-amount-custom" type="number" placeholder="Custom"
                value={custom} onChange={(e) => setCustom(e.target.value)} className={`${input} !w-32 !py-2.5 !rounded-full`} />
            </div>
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="RECIPIENT NAME"><input data-testid="gift-recipient-name" required className={input} value={form.recipient_name} onChange={set('recipient_name')} /></Field>
            <Field label="RECIPIENT EMAIL (OPTIONAL)"><input data-testid="gift-recipient-email" type="email" className={input} value={form.recipient_email} onChange={set('recipient_email')} /></Field>
            <Field label="YOUR NAME"><input data-testid="gift-sender-name" required className={input} value={form.sender_name} onChange={set('sender_name')} /></Field>
            <Field label="YOUR EMAIL"><input data-testid="gift-sender-email" type="email" required className={input} value={form.sender_email} onChange={set('sender_email')} /></Field>
          </div>
          <Field label="PERSONAL NOTE (OPTIONAL)">
            <textarea data-testid="gift-message" maxLength={300} rows={3} className={input} value={form.message} onChange={set('message')} />
          </Field>
          <button data-testid="gift-checkout-btn" disabled={busy}
            className="inline-flex items-center gap-2 bg-[#0F0E0C] hover:bg-[#2A2622] disabled:opacity-60 text-[#F4EFE7] px-7 py-3.5 rounded-full text-[13px] font-medium transition-colors">
            {busy && <Loader2 size={14} className="animate-spin" />} {busy ? 'Opening secure checkout…' : valid ? `Pay $${value} securely` : `Choose $${opts.min}–$${opts.max}`}
          </button>
          <p className="text-[12px] text-[#1E1B18]/45">Secure payment by Stripe. Applicable taxes are calculated at checkout.</p>
        </form>
        <div className="lg:hidden"><BalanceCheck /></div>
      </section>
      <Footer />
    </div>
  );
}
